import { z } from 'zod';

// Enums from PLAN §7.1. Declared as zod enums so the same list validates API
// payloads, database columns and platform settings without drifting apart.

export const platforms = [
  'youtube',
  'instagram',
  'tiktok',
  'facebook',
  'linkedin',
  'telegram',
  'mock',
] as const;
export const platformSchema = z.enum(platforms);
export type Platform = z.infer<typeof platformSchema>;

/** Every platform the app really posts to — `mock` only simulates one of these. */
export const realPlatforms = platforms.filter((p): p is Exclude<Platform, 'mock'> => p !== 'mock');

export const accountKinds = [
  'youtube_channel',
  'instagram_professional',
  'tiktok_creator',
  'facebook_page',
  'linkedin_member',
  'linkedin_organization',
  // One bot can post to many channels, so a channel is the account, not
  // the bot (D-026).
  'telegram_channel',
  'mock',
] as const;
export const accountKindSchema = z.enum(accountKinds);
export type AccountKind = z.infer<typeof accountKindSchema>;

export const connectionStatuses = ['active', 'expiring', 'needs_reconnect', 'revoked'] as const;
export const connectionStatusSchema = z.enum(connectionStatuses);
export type ConnectionStatus = z.infer<typeof connectionStatusSchema>;

export const mediaKinds = ['video', 'image', 'audio'] as const;
export const mediaKindSchema = z.enum(mediaKinds);
export type MediaKind = z.infer<typeof mediaKindSchema>;

export const mediaStatuses = ['uploading', 'processing', 'ready', 'failed'] as const;
export const mediaStatusSchema = z.enum(mediaStatuses);
export type MediaStatus = z.infer<typeof mediaStatusSchema>;

export const storageTiers = ['hot', 'archived', 'deleted'] as const;
export const storageTierSchema = z.enum(storageTiers);
export type StorageTier = z.infer<typeof storageTierSchema>;

export const postTypes = ['video', 'images'] as const;
export const postTypeSchema = z.enum(postTypes);
export type PostType = z.infer<typeof postTypeSchema>;

export const postStatuses = [
  'draft',
  'scheduled',
  'publishing',
  'published',
  'partial',
  'failed',
  'cancelled',
] as const;
export const postStatusSchema = z.enum(postStatuses);
export type PostStatus = z.infer<typeof postStatusSchema>;

export const targetStatuses = [
  'draft',
  'scheduled',
  'queued',
  'running',
  'waiting',
  'published',
  'needs_action',
  'failed',
  'cancelled',
] as const;
export const targetStatusSchema = z.enum(targetStatuses);
export type TargetStatus = z.infer<typeof targetStatusSchema>;

/** The three BullMQ queues from PLAN §5.3. */
export const queueNames = ['media', 'publish', 'maintenance', 'webhook'] as const;
export type QueueName = (typeof queueNames)[number];

/**
 * A webhook is a notification, not the publish. It retries a receiver that
 * is down for a while and then gives up — five attempts over roughly an
 * hour. The post went out either way, and `target_events` is the record.
 */
export const webhookRetryOptions = {
  attempts: 5,
  backoff: { type: 'exponential', delay: 30_000 },
} as const;

/** Media stages retry short-lived database and network outages before giving up. */
export const mediaJobRetryOptions = {
  attempts: 3,
  backoff: { type: 'exponential', delay: 5_000 },
} as const;

/** The single error shape every API route returns (PLAN §14). */
export interface ApiErrorBody {
  error: {
    code: string;
    message: string;
    details?: unknown;
  };
}
